import ArrowBack from "../../Components/ArrowBack";
import styles from "./ProfilePage.module.css";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { accessProfileApi, getProfileInformation } from "../../api/profile";
import { CandidateProfile, SalaryInfo } from "../../models/profile";
import { formatDateInS3 } from "../../constants/constants";
import { useSnackbar } from "../../context/SnackbarContext";
import {
  getApplicantStatusConfig,
  bulkUpdateApplicantStatus,
  StatusConfig,
  getJobApplications,
} from "../../api/applicants";
import { getJobDetails } from "../../api/job";
import { Divider } from "@mui/material";
import {
  differenceInMonths,
  differenceInYears,
  format,
  parseISO,
} from "date-fns";
import downloadIcon from "../../assets/redesign/downloadIcon.svg";
import { MuiTooltip } from "../Tooltip/muitooltip";
import StatusDropDown from "../../Components/StatusDropDown/StatusDropDown";
import { useMasterData } from "../../context/MasterContextProvider";
import clsx from "clsx";
import { useRegion } from "../../context/RegionContext";
import { REGIONS } from "../../constants/regions";
import { icons } from "../../assets/images";

// Format a salary as "amount currencyName" (e.g. "15000 AED"), falling back gracefully.
const formatSalary = (salary?: SalaryInfo, fallbackAmount?: number): string => {
  const amount = salary?.amount ?? fallbackAmount;
  if (amount == null) return "-";
  return salary?.currencyName ? `${amount} ${salary.currencyName}` : `${amount}`;
};

const ProfilePage = () => {
  const [accessed, setAccessed] = useState(false);
  const { region } = useRegion();
  const handleAccess = () => {
    // accessProfile();
    // setAccessed((prevState) => !prevState);
  };

  const [searchParams] = useSearchParams();
  const profileId = searchParams.get("id");
  const from = searchParams.get("from");
  const appId = searchParams.get("appId");
  const jobId = searchParams.get("jobId");
  let status = searchParams.get("status");

  const [profile, setProfile] = useState<CandidateProfile | undefined>(
    undefined
  );
  const [statusConfig, setStatusConfig] = useState<StatusConfig[]>([]);
  const [currentAppStatus, setCurrentAppStatus] = useState<string>("");

  // type Status =
  //   | "applied"
  //   | "onHold"
  //   | "screening"
  //   | "hired"
  //   | "interview"
  //   | "shortlisted"
  //   | "offered"
  //   | "rejected"
  //   | "nonEmirati";

  const onUpdateStatus = async (newStatus: string, applicationId: string) => {
    try {
      const resp = await bulkUpdateApplicantStatus([applicationId], newStatus);

      if (resp?.responseInfo?.httpCode === 200) {
        showSnackbar("Updated Status Successfully", "success");
        status = newStatus;
        setCurrentAppStatus(newStatus); // Update local state
      } else {
        showSnackbar(
          resp?.responseInfo?.description || "Something went wrong",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar("Error updating status", "error");
    }
  };

  const handleStatusChange = (selectedLabel: string, appId: string) => {
    // Find the status config that matches the selected label
    const selectedStatus = statusConfig.find(
      (config) => config.label === selectedLabel
    );
    if (selectedStatus) {
      onUpdateStatus(selectedStatus.fieldKey, appId);
    }
  };

  const getProfileInfo = async () => {
    if (profileId) {
      const resp = await getProfileInformation(profileId);
      if (resp) {
        if (resp.contactInformation?.email !== "******") {
          setAccessed(true);
        } else {
          setAccessed(false);
        }
        setProfile(resp);
      }
    }
  };

  const { showSnackbar } = useSnackbar();

  const accessProfile = async () => {
    const resp = await accessProfileApi(profileId || "");
    if (resp.responseInfo.httpCode === 200) {
      showSnackbar("Contact Accessed successfully", "success");
      getProfileInfo();
      setAccessed((prevState) => !prevState);
    } else {
      showSnackbar(resp.responseInfo.description, "error");
    }
  };

  const fetchStatusConfig = async () => {
    if (jobId) {
      try {
        const response = await getApplicantStatusConfig(jobId);
        if (response?.data?.statusConfig) {
          const sortedConfig = response.data.statusConfig.sort(
            (a: StatusConfig, b: StatusConfig) => a.order - b.order
          );
          setStatusConfig(sortedConfig.filter((config) => config.isVisible));
        }
      } catch (error) {
        console.error("Error fetching status config:", error);
      }
    }
  };

  const fetchCurrentApplicationStatus = async () => {
    if (jobId && appId) {
      try {
        // First get job details to get the job name
        const jobResponse = await getJobDetails(jobId);
        if (jobResponse?.data?.jobName) {
          // Then get applications to find the specific application status
          const applicationsResponse = await getJobApplications(
            jobResponse.data.jobName,
            {
              page: 1,
              limit: 1000, // Large limit to ensure we find the application
              jobId: jobId,
            }
          );

          if (applicationsResponse?.data?.applications) {
            const currentApplication =
              applicationsResponse.data.applications.find(
                (app: any) => app.applicationId === appId
              );

            if (currentApplication) {
              setCurrentAppStatus(
                currentApplication.applicationStatus || "applied"
              );
            }
          }
        }
      } catch (error) {
        console.error("Error fetching current application status:", error);
      }
    }
  };

  useEffect(() => {
    getProfileInfo();
    fetchStatusConfig();
    fetchCurrentApplicationStatus();
  }, [profileId, jobId, appId]);

  const [showAllWorkExperiences, setShowAllWorkExperiences] = useState(false);

  const handleShowMoreClick = () => {
    setShowAllWorkExperiences((prevState) => !prevState);
  };

  const fileSizeInKB = ((profile?.familyBook?.fileSize || 0) / 1024).toFixed(0); // Converted to KB and rounded

  // Format dates
  const formattedCreatedDate = format(
    new Date(profile?.familyBook?.createdDate || 0),
    "dd MMM yyyy"
  );

  // Get the filename and size in the format you requested
  const formattedFileNameAndSize = `${fileSizeInKB} KB | ${formattedCreatedDate}`;

  const statusMap = {
    applied: "Applied",
    onHold: "On Hold",
    screening: "Screening",
    hired: "Hired",
    interview: "Interview",
    shortlisted: "Shortlisted",
    offered: "Offered",
    rejected: "Rejected",
    nonEmirati: "Non-Emirati",
  };

  // Create dynamic status options from the fetched config
  const statusOptions = statusConfig.map((config) => ({
    key: config.fieldKey,
    label: config.label,
    order: config.order,
  }));

  // Get current status label from config
  const getCurrentStatusLabel = () => {
    if (statusOptions.length === 0) return "Applied";

    // Use currentAppStatus (from API) if available, otherwise fallback to URL status param
    const statusToUse = currentAppStatus || status;
    if (!statusToUse) return "Applied";

    const currentStatus = statusConfig.find(
      (config) => config.fieldKey === statusToUse
    );
    return currentStatus?.label || "Applied";
  };

  type StatusMap = {
    applied: string;
    onHold: string;
    screening: string;
    hired: string;
    interview: string;
    shortlisted: string;
    offered: string;
    rejected: string;
    nonEmirati: string;
  };

  const { countries, languages } = useMasterData();

  function getFlagByCountry(name: string): string {
    const country = countries?.find((country) => country.countryName === name);
    return country ? country?.flagUrl : "Entry";
  }

  function getFlagByLanguage(name: string): string {
    const language = languages?.find((language) => language.language === name);
    return language ? language?.flagUrl : "Entry";
  }

  const primaryJobTitle =
    profile?.jobTitle?.trim() || profile?.jobTitleEn?.trim() || "-";
  const showTranslatedJobTitle =
    !!profile?.jobTitle?.trim() &&
    !!profile?.jobTitleEn?.trim() &&
    profile.jobTitle.trim() !== profile.jobTitleEn.trim();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <ArrowBack to={from || ".."} title="Candidate Profile" />
        {/* Desktop buttons - shown on desktop only */}
        <div className={styles.headerBtnsDesktop}>
          {from?.includes("application") && (
            <StatusDropDown
              value={
                jobId
                  ? getCurrentStatusLabel()
                  : statusMap[status as keyof StatusMap] || "Applied"
              }
              onChange={handleStatusChange}
              appId={appId || ""}
              statusOptions={
                jobId && statusOptions.length > 0 ? statusOptions : undefined
              }
            />
          )}
          {accessed ? (
            <div onClick={handleAccess} className={styles.accessedBtn}>
              Accessed
            </div>
          ) : (
            <div onClick={accessProfile} className={styles.accessBtn}>
              Access Contacts
            </div>
          )}
        </div>
      </div>

      {/* Mobile buttons - shown below title on mobile only */}
      <div className={styles.headerBtnsMobile}>
        {from?.includes("application") && (
          <StatusDropDown
            value={
              jobId
                ? getCurrentStatusLabel()
                : statusMap[status as keyof StatusMap] || "Applied"
            }
            onChange={handleStatusChange}
            appId={appId || ""}
            statusOptions={
              jobId && statusOptions.length > 0 ? statusOptions : undefined
            }
          />
        )}
        {accessed ? (
          <div onClick={handleAccess} className={styles.accessedBtn}>
            Accessed
          </div>
        ) : (
          <div onClick={accessProfile} className={styles.accessBtn}>
            Access Contacts
          </div>
        )}
      </div>

      {/* Mobile-only Basic Information */}
      <div className={styles.mobileBasicInfo}>
        <div className={styles.mobileAvatarSection}>
          {profile?.profilePicture ? (
            <img src={profile.profilePicture} className={styles.profileImg} />
          ) : (
            <div className={styles.initials}>AM</div>
          )}
          <div className={styles.mobileNameSection}>
            <span className={styles.infoTitle}>Full Name</span>
            <span className={styles.infoContent}>
              {profile?.displayName || "-"}
            </span>
          </div>
        </div>

        <div className={styles.mobileInfoGrid}>
          <div className={styles.mobileInfoItem}>
            <span className={styles.infoTitle}>Job title</span>
            <span className={styles.infoContent}>
              {primaryJobTitle}
            </span>
            {showTranslatedJobTitle && (
              <div className={styles.inlineTranslatedWrapper}>
                <span className={styles.inlineTranslatedLabel}>
                  Translated to English
                </span>
                <span className={styles.inlineTranslatedValue}>
                  {profile?.jobTitleEn}
                </span>
              </div>
            )}
          </div>
          {(profile?.currentSalary?.amount != null ||
            profile?.salary != null) && (
            <div className={styles.mobileInfoItem}>
              <span className={styles.infoTitle}>Current Salary</span>
              <span className={styles.infoContent}>
                {formatSalary(profile?.currentSalary, profile?.salary)}
              </span>
            </div>
          )}
          {profile?.expectedSalary?.amount != null && (
            <div className={styles.mobileInfoItem}>
              <span className={styles.infoTitle}>Expected Salary</span>
              <span className={styles.infoContent}>
                {formatSalary(profile?.expectedSalary)}
              </span>
            </div>
          )}
          {profile?.noticePeriodInDays != null && (
            <div className={styles.mobileInfoItem}>
              <span className={styles.infoTitle}>Notice Period</span>
              <span className={styles.infoContent}>
                {`${profile.noticePeriodInDays} days`}
              </span>
            </div>
          )}
          <div className={styles.mobileInfoItem}>
            <span className={styles.infoTitle}>Nationality</span>
            <span className={styles.infoContent}>
              <span className={styles.rowCenter}>
                {profile?.nationality || "-"}{" "}
                {profile?.nationality && (
                  <img
                    className={styles.flag}
                    src={getFlagByCountry(profile?.nationality)}
                  />
                )}
              </span>
            </span>
          </div>
          <div className={styles.mobileInfoItem}>
            <span className={styles.infoTitle}>
              {region === REGIONS.AE ? "Emirate" : "Province"}
            </span>
            <span className={styles.infoContent}>
              {profile?.emirateOrState || "-"}
            </span>
          </div>
          <div className={styles.mobileInfoItem}>
            <span className={styles.infoTitle}>Date of birth</span>
            <span className={styles.infoContent}>
              {profile?.dateOfBirth
                ? formatDateInS3(profile?.dateOfBirth || "")
                : "-"}
            </span>
          </div>
          <div className={styles.mobileInfoItem}>
            <span className={styles.infoTitle}>Gender</span>
            <span className={styles.infoContent}>{profile?.gender || "-"}</span>
          </div>
        </div>
      </div>

      <p className={styles.bold}>Basic Information</p>
      <div className={styles.infoContainer}>
        <div className={styles.firstCol}>
          {profile?.profilePicture ? (
            <img src={profile.profilePicture} className={styles.profileImg} />
          ) : (
            <div className={styles.initials}>AM</div>
          )}
          <div className={styles.displayColumn}>
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>Full Name</span>
              <span className={styles.infoContent}>
                {profile?.displayName || "-"}
              </span>
            </div>
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>Nationality</span>
              <span className={styles.infoContent}>
                {profile?.nationality || "-"}{" "}
                {profile?.nationality && (
                  <span>
                    <img
                      className={styles.flag}
                      src={getFlagByCountry(profile?.nationality)}
                    />
                  </span>
                )}
              </span>
            </div>
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>Date of birth</span>
              <span className={styles.infoContent}>
                {" "}
                {profile?.dateOfBirth
                  ? formatDateInS3(profile?.dateOfBirth || "")
                  : "-"}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.displayColumn}>
          <div className={styles.infoWrapper}>
            <span className={styles.infoTitle}>Job title</span>
            <span className={styles.infoContent}>
              {primaryJobTitle}
            </span>
            {showTranslatedJobTitle && (
              <div className={styles.inlineTranslatedWrapper}>
                <span className={styles.inlineTranslatedLabel}>
                  Translated to English
                </span>
                <span className={styles.inlineTranslatedValue}>
                  {profile?.jobTitleEn}
                </span>
              </div>
            )}
          </div>
          <div className={styles.infoWrapper}>
          <span className={styles.infoTitle}>
  {region === REGIONS.SA ? "Provinces" : "Emirate"}
</span>
            <span className={styles.infoContent}>
              {profile?.emirateOrState || "-"}
            </span>
          </div>
        </div>
        <div className={styles.displayColumn}>
          {(profile?.currentSalary?.amount != null ||
            profile?.salary != null) && (
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>Current Salary</span>
              <span className={styles.infoContent}>
                {formatSalary(profile?.currentSalary, profile?.salary)}
              </span>
            </div>
          )}
          {profile?.expectedSalary?.amount != null && (
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>Expected Salary</span>
              <span className={styles.infoContent}>
                {formatSalary(profile?.expectedSalary)}
              </span>
            </div>
          )}
          {profile?.noticePeriodInDays != null && (
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>Notice Period</span>
              <span className={styles.infoContent}>
                {`${profile.noticePeriodInDays} days`}
              </span>
            </div>
          )}
          <div className={styles.infoWrapper}>
            <span className={styles.infoTitle}>Gender</span>
            <span className={styles.infoContent}>{profile?.gender || "-"}</span>
          </div>
        </div>
      </div>
      <p className={styles.bold}>Contact Information</p>
      {accessed ? (
        <div className={styles.contactContainer}>
          <div className={styles.infoWrapper}>
            <span className={styles.infoTitle}>Mobile</span>
            <span className={styles.infoContent}>
              {profile?.contactInformation?.mobileNumber || "-"}
            </span>
          </div>
          <div className={styles.infoWrapper}>
            <span className={styles.infoTitle}>Email</span>
            <span className={styles.infoContent}>
              {profile?.contactInformation?.email || "-"}
            </span>
          </div>
          <div className={styles.rowCenter}>
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>CV</span>
              <span className={styles.infoCvWrapper}>
                <img
                  src={icons.pdf}
                  className={styles.cv}
                />
                <div className={styles.infoWrapper}>
                  <span className={styles.infoContent}>
                    {profile?.contactInformation?.fullName || "-"}
                  </span>
                </div>
              </span>
            </div>
            {profile?.contactInformation?.resumeUrl && (
              <Link
                className="noUnderline"
                to={profile?.contactInformation.resumeUrl}
                target="_blank"
              >
                <div className={styles.downloadBtn}>
                  <img
                    src={downloadIcon}
                    alt="download"
                    className={styles.downloadIcon}
                  />
                  <div>Download</div>
                </div>
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.contactContainerBlurred}>
          <div className={styles.infoWrapper}>
            <span className={styles.infoTitle}>Mobile</span>
            <span className={styles.infoContent}>+971 50 123 4567</span>
          </div>
          <div className={styles.infoWrapper}>
            <span className={styles.infoTitle}>Email</span>
            <span className={styles.infoContent}>google@ak.com</span>
          </div>
          <div className={styles.rowCenter}>
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>CV</span>
              <span className={styles.infoCvWrapper}>
                <img
                  src={icons.pdf}
                  className={styles.cv}
                />
                <div className={styles.infoWrapper}>
                  <span className={styles.infoContent}>AK</span>
                  <span className={styles.infoTitle}>837tb | 31 Feb 2088</span>
                </div>
              </span>
            </div>
            <div className={styles.accessBtn}>Download</div>
          </div>
        </div>
      )}
      {profile?.bio && (
        <div>
          <p className={styles.bold}>Bio</p>
          <div className={styles.bioContainer}>{profile?.bio}</div>
        </div>
      )}
      {profile?.workExperiences && profile.workExperiences[0] && (
        <div>
          <p className={styles.bold}>Work Experience</p>
          <div className={styles.bioContainer}>
            <div className={styles.infoWrapper}>
              <span className={styles.infoTitle}>
                Total Years of experience
              </span>
              <span className={styles.infoContent}>
                {profile.totalExperienceYears}
              </span>
            </div>
            <br />
            <Divider />
            {profile.workExperiences
              ?.reverse()
              ?.slice(
                0,
                showAllWorkExperiences ? profile.workExperiences.length : 2
              )
              .map((exp, idx) => {
                const start = parseISO(
                  exp?.startDate || new Date().toISOString()
                );
                const end = exp.isCurrent
                  ? new Date()
                  : parseISO(exp?.endDate || new Date().toISOString()); // Use today's date if the job is current

                // Calculate the difference in years
                const years = differenceInYears(end, start);

                // Format start and end dates as "Jan 2015 - Feb 2022"
                const formattedStartDate = format(start, "MMM yyyy");
                const formattedEndDate = format(end, "MMM yyyy");

                const months = differenceInMonths(end, start) % 12;

                // Display the date range and work duration
                const workTime = `${formattedStartDate} - ${formattedEndDate} | ${years} Years, ${months} months`;

                return (
                  <div key={idx} className={styles.workWrapper}>
                    <br />
                    <span className={styles.workTitle}>
                      {exp.jobTitle || ""} @ {exp.companyName || ""}
                    </span>
                    {exp.jobTitleEn?.trim() && (
                      <div className={styles.translatedWorkWrapper}>
                        <span className={styles.translatedLabel}>
                          Translated to English
                        </span>
                        <span className={styles.translatedWorkTitle}>
                          {exp.jobTitleEn} @ {exp.companyName || ""}
                        </span>
                      </div>
                    )}
                    <span className={styles.workTime}>{workTime}</span>
                    <span className={styles.workDesc}>
                      {exp.jobDescription || "-"}
                    </span>
                    {(showAllWorkExperiences
                      ? profile.workExperiences.length
                      : profile.workExperiences.length < 3
                      ? 0
                      : 2) >
                      idx + 1 && <br />}
                    {(showAllWorkExperiences
                      ? profile.workExperiences.length
                      : profile.workExperiences.length < 3
                      ? 0
                      : 2) >
                      idx + 1 && <Divider />}
                  </div>
                );
              })}
            {profile.workExperiences.length > 2 && (
              <div className={styles.showMoreBtnContainer}>
                <button
                  className={styles.showMoreBtn}
                  onClick={handleShowMoreClick}
                >
                  {showAllWorkExperiences
                    ? "Show Less"
                    : `See All ${profile.workExperiences.length} experiences`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={styles.flexContainer}>
        {profile?.education &&
          profile.education.filter(
            (edu) =>
              edu.institution ||
              edu.degree ||
              edu.fieldOfStudy ||
              edu.fieldOfStudyEn
          ).length > 0 && (
            <div>
              <p className={styles.bold}>Education</p>
              <div className={styles.bioContainer}>
                {profile.education
                  .filter(
                    (edu) =>
                      edu.institution ||
                      edu.degree ||
                      edu.fieldOfStudy ||
                      edu.fieldOfStudyEn
                  )
                  .map((edu, idx) => {
                    const isValidDate = (
                      date: Date | string | null | undefined
                    ) => {
                      if (!date) return false;
                      try {
                        const dateObj =
                          typeof date === "string" ? parseISO(date) : date;
                        return (
                          !isNaN(dateObj.getTime()) &&
                          dateObj.getFullYear() > 1970
                        );
                      } catch {
                        return false;
                      }
                    };

                    const hasValidStartDate = isValidDate(edu.startDate);
                    const hasValidEndDate = isValidDate(edu.endDate);
                    const hasValidDates = hasValidStartDate && hasValidEndDate;

                    let dateDisplay = "";
                    if (hasValidDates) {
                      try {
                        const startDate =
                          typeof edu.startDate === "string"
                            ? parseISO(edu.startDate)
                            : edu.startDate!;
                        const endDate =
                          typeof edu.endDate === "string"
                            ? parseISO(edu.endDate)
                            : edu.endDate!;
                        const years = differenceInYears(endDate, startDate);
                        const months =
                          differenceInMonths(endDate, startDate) % 12;
                        dateDisplay = `${format(
                          startDate,
                          "MMM yyyy"
                        )} - ${format(
                          endDate,
                          "MMM yyyy"
                        )} | ${years} Years, ${months} months`;
                      } catch {
                        dateDisplay = "";
                      }
                    }

                    const primaryFieldOfStudy =
                      edu.fieldOfStudy?.trim() || edu.fieldOfStudyEn?.trim() || "-";
                    const showTranslatedFieldOfStudy =
                      !!edu.fieldOfStudy?.trim() &&
                      !!edu.fieldOfStudyEn?.trim() &&
                      edu.fieldOfStudy.trim() !== edu.fieldOfStudyEn.trim();

                    return (
                      <div key={idx} className={styles.workWrapper}>
                        {idx > 0 && <br />}
                        <span className={styles.workTitle}>
                          {edu.institution}
                        </span>
                        {dateDisplay && (
                          <span className={styles.workDesc}>
                            {" "}
                            {dateDisplay}
                          </span>
                        )}
                        <span className={styles.workDesc}>{edu.degree}</span>
                        <span className={styles.workTime}>
                          {primaryFieldOfStudy}
                        </span>
                        {showTranslatedFieldOfStudy && (
                          <div className={styles.inlineTranslatedWrapper}>
                            <span className={styles.inlineTranslatedLabel}>
                              Translated to English
                            </span>
                            <span className={styles.inlineTranslatedValue}>
                              {edu.fieldOfStudyEn}
                            </span>
                          </div>
                        )}
                        {profile.education.filter(
                          (edu) =>
                            edu.institution ||
                            edu.degree ||
                            edu.fieldOfStudy ||
                            edu.fieldOfStudyEn
                        ).length >
                          idx + 1 && <br />}
                        {profile.education.filter(
                          (edu) =>
                            edu.institution ||
                            edu.degree ||
                            edu.fieldOfStudy ||
                            edu.fieldOfStudyEn
                        ).length >
                          idx + 1 && <Divider />}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        {profile?.certifications && profile.certifications[0] && (
          <div>
            <p className={clsx(styles.bold, styles.mobileFriendly)}>
              Certificates/Achievements
            </p>
            <div className={styles.bioContainer}>
              {profile.certifications.map((cert, idx) => {
                const isValidDate = (dateString: string | null | undefined) => {
                  if (!dateString) return false;
                  try {
                    const date = parseISO(dateString);
                    return !isNaN(date.getTime()) && date.getFullYear() > 1970;
                  } catch {
                    return false;
                  }
                };

                const hasValidStartDate = isValidDate(cert?.startDate);
                const hasValidEndDate = isValidDate(cert?.endDate);
                const hasValidDates = hasValidStartDate && hasValidEndDate;

                let workTime = "";
                if (hasValidDates) {
                  try {
                    const start = parseISO(cert.startDate!);
                    const end = parseISO(cert.endDate!);
                    const years = differenceInYears(end, start);
                    const months = differenceInMonths(end, start) % 12;
                    const formattedStartDate = format(start, "MMM yyyy");
                    const formattedEndDate = format(end, "MMM yyyy");
                    workTime = `${formattedStartDate} - ${formattedEndDate} | ${years} Years, ${months} months`;
                  } catch {
                    workTime = "";
                  }
                } else if (hasValidEndDate) {
                  try {
                    const end = parseISO(cert.endDate!);
                    workTime = `Completed: ${format(end, "MMM yyyy")}`;
                  } catch {
                    workTime = "";
                  }
                }

                return (
                  <div key={idx} className={styles.workWrapper}>
                    {idx > 0 && <br />}
                    <span className={styles.workTitle}>
                      {cert.institution || "Certification"}
                    </span>
                    {workTime && (
                      <span className={styles.workTime}>{workTime}</span>
                    )}
                    <span className={styles.workDesc}>
                      {cert.name || "Certificate"}
                    </span>
                    {profile.certifications.length > idx + 1 && <br />}
                    {profile.certifications.length > idx + 1 && <Divider />}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {profile?.familyBook && (
        <div>
          <p className={styles.bold}>Family Book</p>
          <div className={styles.contactContainer}>
            <span className={styles.infoCvWrapper}>
              <img
                src={icons.pdf}
                className={styles.cv}
              />
              <div className={styles.infoWrapper}>
                <span className={styles.infoContent}>
                  {profile.familyBook.fileName}
                </span>
                <span className={styles.infoTitle}>
                  {formattedFileNameAndSize}
                </span>
              </div>
            </span>
            {profile?.familyBook.fileUrl && (
              <Link
                className="noUnderline"
                to={profile?.familyBook.fileUrl}
                target="_blank"
              >
                <div className={styles.downloadBtn}>
                  <img
                    src={downloadIcon}
                    alt="download"
                    className={styles.downloadIcon}
                  />
                  <div>Download</div>
                </div>
              </Link>
            )}
          </div>
        </div>
      )}
      <div className={styles.flexContainer}>
        {profile?.skills && profile?.skills[0] && (
          <div>
            <p className={styles.bold}>Skills</p>
            <div className={styles.bioContainer}>
              <div className={styles.chipsContainer}>
                {profile.skills?.map((skill, idx) => {
                  return (
                    <span className={styles.skill} key={idx}>
                      {skill}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {profile?.languages && profile?.languages[0] && (
          <div>
            <p className={styles.bold}>Languages</p>
            <div className={styles.bioContainer}>
              <div className={styles.chipsContainer}>
                {profile.languages?.map((language, idx) => {
                  return (
                    <MuiTooltip title={<span>{language.proficiency}</span>}>
                      <span className={styles.skill} key={idx}>
                        {language.name}{" "}
                        {language?.name && (
                          <span>
                            <img
                              className={styles.flag}
                              src={getFlagByLanguage(language?.name)}
                            />
                          </span>
                        )}
                      </span>
                    </MuiTooltip>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
